import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// ── Floating Dots Canvas ─────────────────────────────────────────────
const FloatingDots = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let dots = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
    };

    const createDots = () => {
      dots = [];
      const count = Math.floor((canvas.width * canvas.height) / 18000);
      for (let i = 0; i < count; i++) {
        dots.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 1.2 + 0.3,
          speed: Math.random() * 0.3 + 0.1,
          opacity: Math.random() * 0.4 + 0.1,
          drift: (Math.random() - 0.5) * 0.15,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach(d => {
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139, 92, 246, ${d.opacity})`;
        ctx.fill();
        d.y -= d.speed;
        d.x += d.drift;
        if (d.y < -10) {
          d.y = canvas.height + 10;
          d.x = Math.random() * canvas.width;
        }
        if (d.x < -10) d.x = canvas.width + 10;
        if (d.x > canvas.width + 10) d.x = -10;
      });
      animationId = requestAnimationFrame(draw);
    };

    resize();
    createDots();
    draw();

    window.addEventListener('resize', () => { resize(); createDots(); });
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
};

// ── Navbar ───────────────────────────────────────────────────────────
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Security', href: '#security' },
    { label: 'For Attorneys', href: '#attorneys' },
  ];

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-slate-950/80 backdrop-blur-xl border-b border-white/5 shadow-2xl shadow-indigo-500/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="#hero" onClick={e => scrollTo(e, '#hero')} className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25 group-hover:shadow-indigo-500/40 transition-shadow">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <span className="text-xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
            FamilyTrails
          </span>
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-1">
          {links.map(l => (
            <a
              key={l.href}
              href={l.href}
              onClick={e => scrollTo(e, l.href)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-all duration-300"
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/auth"
            className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
          >
            Log In
          </Link>
          <Link
            to="/auth"
            className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl hover:from-indigo-400 hover:to-purple-500 transition-all shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5"
          >
            Get Started
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 text-slate-400 hover:text-white"
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {mobileOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </>
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-80 border-b border-white/5' : 'max-h-0'
        }`}
        style={{ background: 'rgba(2, 6, 23, 0.95)', backdropFilter: 'blur(20px)' }}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {links.map(l => (
            <a
              key={l.href}
              href={l.href}
              onClick={e => scrollTo(e, l.href)}
              className="py-3 text-slate-400 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
          <div className="flex gap-3 pt-4 border-t border-white/5 mt-2">
            <Link to="/auth" className="flex-1 py-2.5 text-center text-sm text-slate-300 border border-white/10 rounded-xl hover:bg-white/5 transition-all">
              Log In
            </Link>
            <Link to="/auth" className="flex-1 py-2.5 text-center text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

// ── Section Wrapper ──────────────────────────────────────────────────
const Section = ({ id, children, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id={id}
      ref={ref}
      className={`transition-all duration-1000 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      } ${className}`}
    >
      {children}
    </section>
  );
};

// ── Feature Card ─────────────────────────────────────────────────────
const FeatureCard = ({ icon, title, desc, delay }) => (
  <div
    className="group relative p-6 rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-indigo-500/20 transition-all duration-500"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
      <span className="text-indigo-400 text-lg">{icon}</span>
    </div>
    <h3 className="text-white font-semibold mb-2">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
  </div>
);

// ── Step Card ────────────────────────────────────────────────────────
const StepCard = ({ num, title, desc }) => (
  <div className="relative flex gap-5">
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-indigo-500/25 flex-shrink-0">
        {num}
      </div>
      {num < 4 && <div className="w-px h-full bg-gradient-to-b from-indigo-500/30 to-transparent mt-2" />}
    </div>
    <div className="pb-12">
      <h3 className="text-white font-semibold mb-1.5">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
    </div>
  </div>
);

// ── Main Landing Page ────────────────────────────────────────────────
const LandingPage = () => {
  return (
    <div className="relative min-h-screen bg-slate-950 text-white overflow-hidden">
      <FloatingDots />

      {/* ── Mesh Gradient Blobs ─────────────────────── */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {/* Top-left indigo */}
        <div
          className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full opacity-[0.07]"
          style={{
            background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)',
            animation: 'meshFloat1 20s ease-in-out infinite',
          }}
        />
        {/* Center-right purple */}
        <div
          className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.06]"
          style={{
            background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)',
            animation: 'meshFloat2 25s ease-in-out infinite',
          }}
        />
        {/* Bottom-left teal */}
        <div
          className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full opacity-[0.05]"
          style={{
            background: 'radial-gradient(circle, #14b8a6 0%, transparent 70%)',
            animation: 'meshFloat3 22s ease-in-out infinite',
          }}
        />
      </div>

      {/* Content sits above gradients & dots */}
      <div className="relative" style={{ zIndex: 2 }}>
        <Navbar />

        {/* ══════════════════════════════════════════════════════════════
            HERO
           ══════════════════════════════════════════════════════════════ */}
        <section id="hero" className="min-h-screen flex items-center justify-center px-6 pt-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-indigo-500/20 bg-indigo-500/5 mb-8 animate-fadeInUp">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs text-slate-400 tracking-wide">Court-Admissible Co-Parenting Platform</span>
            </div>

            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6 animate-fadeInUp"
              style={{ animationDelay: '100ms' }}
            >
              Co-parent with{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradientShift">
                clarity
              </span>
              ,<br />
              not conflict.
            </h1>

            <p
              className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed animate-fadeInUp"
              style={{ animationDelay: '200ms' }}
            >
              Secure messaging, shared calendars, expense tracking, and court-ready documentation — all in one place.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp"
              style={{ animationDelay: '300ms' }}
            >
              <Link
                to="/auth"
                className="group px-8 py-3.5 text-base font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl hover:from-indigo-400 hover:to-purple-500 transition-all shadow-xl shadow-indigo-500/20 hover:shadow-indigo-500/30 hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                Get Started Free
                <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </Link>
              <Link
                to="/auth"
                className="px-8 py-3.5 text-base font-medium text-slate-300 border border-white/10 rounded-2xl hover:bg-white/5 hover:border-white/20 transition-all hover:-translate-y-0.5"
              >
                Log In
              </Link>
            </div>

            {/* Subtle scroll indicator */}
            <div className="mt-20 animate-bounce opacity-30">
              <svg className="w-5 h-5 mx-auto text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7"/>
              </svg>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════
            FEATURES
           ══════════════════════════════════════════════════════════════ */}
        <Section id="features" className="py-32 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-indigo-400 mb-3">Features</p>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Everything you need,{' '}
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">nothing you don't.</span>
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Built for families navigating co-parenting. Designed for clarity and accountability.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <FeatureCard
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>}
                title="Encrypted Messaging"
                desc="End-to-end encrypted messages with AI tone analysis. Every exchange is court-admissible."
                delay={0}
              />
              <FeatureCard
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
                title="Shared Calendar"
                desc="Coordinate custody schedules, appointments, and handoffs with GPS check-in verification."
                delay={100}
              />
              <FeatureCard
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
                title="Expense Tracking"
                desc="Log, split, and approve shared expenses. Attach receipts. Generate reports for court."
                delay={200}
              />
              <FeatureCard
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                title="Document Vault"
                desc="Securely store and share custody agreements, medical records, and school documents."
                delay={300}
              />
              <FeatureCard
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15.05 5A5 5 0 0119 8.95M15.05 1A9 9 0 0123 8.94M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>}
                title="Video & Voice Calls"
                desc="Built-in calling with optional recording. No need for third-party apps."
                delay={400}
              />
              <FeatureCard
                icon={<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
                title="Court-Ready Reports"
                desc="Timestamped logs, clinical scoring, and exportable reports attorneys can use in proceedings."
                delay={500}
              />
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════
            HOW IT WORKS
           ══════════════════════════════════════════════════════════════ */}
        <Section id="how-it-works" className="py-32 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-xs uppercase tracking-[0.2em] text-indigo-400 mb-3">How It Works</p>
              <h2 className="text-3xl sm:text-4xl font-bold">
                Up and running in{' '}
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">minutes.</span>
              </h2>
            </div>

            <div className="ml-2">
              <StepCard
                num={1}
                title="Your attorney sends an invite"
                desc="Your attorney creates your account through a secure referral link. No self-signup — this keeps the platform trusted and verified."
              />
              <StepCard
                num={2}
                title="Set up your profile"
                desc="Complete your profile, add your children's information, and connect with your co-parent on the platform."
              />
              <StepCard
                num={3}
                title="Start communicating"
                desc="Use encrypted messaging, shared calendars, and expense tracking. Every interaction is logged and timestamped."
              />
              <StepCard
                num={4}
                title="Your attorney monitors progress"
                desc="Attorneys get a professional dashboard with clinical insights, engagement scores, and court-ready reports."
              />
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════
            SECURITY
           ══════════════════════════════════════════════════════════════ */}
        <Section id="security" className="py-32 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-3xl border border-white/5 bg-white/[0.02] p-10 sm:p-16 overflow-hidden">
              {/* Background glow */}
              <div className="absolute top-0 right-0 w-80 h-80 bg-indigo-500/5 rounded-full blur-3xl" />

              <div className="relative grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-indigo-400 mb-3">Security</p>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-5">
                    Your data is{' '}
                    <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">untouchable.</span>
                  </h2>
                  <p className="text-slate-400 leading-relaxed mb-6">
                    Built with the same security standards as healthcare platforms. Row-level data isolation ensures families can never see each other's information.
                  </p>
                  <Link
                    to="/auth"
                    className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors group"
                  >
                    Start securely
                    <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                    </svg>
                  </Link>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Encryption', sub: 'AES-256 at rest' },
                    { label: 'Isolation', sub: 'Row-level security' },
                    { label: 'Audit Trail', sub: 'Every action logged' },
                    { label: 'Compliance', sub: 'Court-admissible' },
                  ].map((item, i) => (
                    <div key={i} className="p-4 rounded-xl border border-white/5 bg-white/[0.02]">
                      <p className="text-white font-semibold text-sm mb-0.5">{item.label}</p>
                      <p className="text-xs text-slate-500">{item.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════
            FOR ATTORNEYS
           ══════════════════════════════════════════════════════════════ */}
        <Section id="attorneys" className="py-32 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-indigo-400 mb-3">For Attorneys</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5">
              A professional dashboard built for{' '}
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">family law.</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mb-12">
              Refer clients, monitor engagement, review clinical scores, and generate court reports — all from one interface.
            </p>

            <div className="grid sm:grid-cols-3 gap-5 text-left">
              {[
                {
                  title: 'Client Referrals',
                  desc: 'Invite parents via secure link. They can only join through your referral.',
                },
                {
                  title: 'Case Insights',
                  desc: 'Co-parenting index scores, discrepancy analysis, and engagement metrics.',
                },
                {
                  title: 'Court Reports',
                  desc: 'Exportable timelines and compliance reports ready for proceedings.',
                },
              ].map((item, i) => (
                <div key={i} className="p-6 rounded-2xl border border-white/5 bg-white/[0.02] hover:border-indigo-500/20 transition-all duration-500">
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════
            FINAL CTA
           ══════════════════════════════════════════════════════════════ */}
        <Section id="cta" className="py-32 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold mb-5">
              Ready to co-parent{' '}
              <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradientShift">
                better
              </span>
              ?
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-10 text-lg">
              Join families who are turning conflict into clarity.
            </p>
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 px-10 py-4 text-base font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl hover:from-indigo-400 hover:to-purple-500 transition-all shadow-xl shadow-indigo-500/20 hover:shadow-indigo-500/30 hover:-translate-y-0.5"
            >
              Get Started
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </Link>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════
            FOOTER
           ══════════════════════════════════════════════════════════════ */}
        <footer className="border-t border-white/5 py-10 px-6">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </div>
              <span className="text-sm text-slate-500">FamilyTrails</span>
            </div>
            <p className="text-xs text-slate-600">&copy; {new Date().getFullYear()} FamilyTrails. All rights reserved.</p>
          </div>
        </footer>
      </div>

      {/* ── Keyframe Animations (injected via style tag) ────────────── */}
      <style>{`
        @keyframes meshFloat1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(60px, 40px) scale(1.05); }
          66% { transform: translate(-30px, 80px) scale(0.95); }
        }
        @keyframes meshFloat2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-80px, -50px) scale(1.08); }
        }
        @keyframes meshFloat3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(50px, -60px) scale(1.04); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gradientShift {
          0% { background-position: 0% center; }
          50% { background-position: 100% center; }
          100% { background-position: 0% center; }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out both;
        }
        .animate-gradientShift {
          animation: gradientShift 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
